import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bot, Send, Plus, Trash2, Sparkles } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PROMPTS = [
  "Why is my project delayed?",
  "How much have we spent so far?",
  "Compare actual spending with the BOQ.",
  "What materials should we order next?",
  "Which projects are at risk?",
  "Create a client progress update.",
];

export default function Copilot() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const projectId = params.get("project");
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const variant = user?.account_type === "business" ? "business" : "home";

  const loadSessions = async () => {
    const { data } = await api.get("/ai/sessions", { params: projectId ? { project_id: projectId } : {} });
    setSessions(data);
    if (data.length && !active) selectSession(data[0]);
    if (!data.length) newChat();
  };
  useEffect(() => { loadSessions(); /* eslint-disable-line */ }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const newChat = async () => {
    const { data } = await api.post("/ai/sessions", { title: "New chat", project_id: projectId || null });
    setSessions((s) => [data, ...s]);
    setActive(data); setMessages([]);
  };
  const selectSession = async (s) => {
    setActive(s);
    const { data } = await api.get(`/ai/sessions/${s.id}/messages`);
    setMessages(data);
  };
  const del = async (s) => {
    await api.delete(`/ai/sessions/${s.id}`);
    const rest = sessions.filter((x) => x.id !== s.id);
    setSessions(rest);
    if (active?.id === s.id) { rest.length ? selectSession(rest[0]) : newChat(); }
  };

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || !active) return;
    setInput(""); setSending(true);
    setMessages((m) => [...m, { id: "tmp", role: "user", content: msg }]);
    try {
      const { data } = await api.post(`/ai/sessions/${active.id}/chat`, { message: msg });
      setMessages((m) => [...m.filter((x) => x.id !== "tmp"), { id: "u" + Date.now(), role: "user", content: msg }, data.reply]);
      loadSessions();
    } catch {
      setMessages((m) => [...m.filter((x) => x.id !== "tmp"), { id: "u", role: "user", content: msg }, { id: "e", role: "assistant", content: "The AI service is unavailable right now. Please try again." }]);
    } finally { setSending(false); }
  };

  return (
    <AppShell variant={variant} title="Sitepar Intelligence">
      <div className="grid lg:grid-cols-4 gap-4 h-[calc(100vh-9rem)]" data-testid="ai-copilot">
        <div className="hidden lg:flex flex-col rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="p-3 border-b"><Button data-testid="new-chat" onClick={newChat} className="w-full bg-sp-navy hover:bg-sp-charcoal rounded-lg"><Plus className="mr-1 h-4 w-4" />New chat</Button></div>
          <div className="flex-1 overflow-y-auto sp-scroll p-2 space-y-1">
            {sessions.map((s) => (
              <div key={s.id} onClick={() => selectSession(s)} className={`group px-3 py-2 rounded-lg cursor-pointer flex items-center justify-between ${active?.id === s.id ? "bg-sp-orange/10" : "hover:bg-slate-50"}`}>
                <span className="text-sm text-sp-navy truncate">{s.title}</span>
                <button onClick={(e) => { e.stopPropagation(); del(s); }} className="opacity-0 group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 flex flex-col rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="flex-1 overflow-y-auto sp-scroll p-5 space-y-5">
            {messages.length === 0 && !sending && (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="h-14 w-14 rounded-full bg-sp-orange/10 flex items-center justify-center"><Sparkles className="h-6 w-6 text-sp-orange" /></div>
                <h2 className="mt-4 text-xl font-bold text-sp-navy">How can I help with your project?</h2>
                <p className="text-sm text-slate-500 mt-1">Ask about budget, BOQ, delays, materials or quality.</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-2 max-w-xl">
                  {PROMPTS.map((p) => <button key={p} data-testid="prompt-chip" onClick={() => send(p)} className="text-left text-sm px-4 py-2.5 rounded-lg border border-slate-200 hover:border-sp-orange text-slate-600">{p}</button>)}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && <div className="h-8 w-8 rounded-lg bg-sp-navy flex items-center justify-center shrink-0"><Bot className="h-4 w-4 text-sp-orange" /></div>}
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-sp-orange text-white" : "bg-slate-100 text-sp-navy"}`}>{m.content}</div>
              </div>
            ))}
            {sending && <div className="flex gap-3"><div className="h-8 w-8 rounded-lg bg-sp-navy flex items-center justify-center"><Bot className="h-4 w-4 text-sp-orange" /></div><div className="bg-slate-100 rounded-2xl px-4 py-3 text-sm text-slate-400">Thinking...</div></div>}
            <div ref={endRef} />
          </div>
          <div className="border-t p-3">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
              <Input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Sitepar Intelligence..." className="h-11" disabled={sending} />
              <Button data-testid="ai-send" type="submit" disabled={sending || !input.trim()} className="h-11 w-11 p-0 bg-sp-orange hover:bg-sp-orangedark"><Send className="h-4 w-4" /></Button>
            </form>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
